import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Crown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Home social-proof strip: the most recent three Founding plates rendered
 * inline as engraved gold nameplates. Sits right under the Founding
 * countdown ticker so visitors see "someone actually paid" scarcity before
 * they reach /subscribe. Renders nothing until the API responds so it never
 * flashes an empty rail on cold loads.
 */
const HomeFoundersPreview = () => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/founders`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setItems(Array.isArray(d?.items) ? d.items : []))
      .catch(() => setItems([]));
  }, []);

  if (!items || items.length === 0) return null;

  // Most recent three, newest first (server returns oldest-first)
  const last3 = items.slice(-3).reverse();

  return (
    <section
      data-testid="home-founders-preview"
      className="max-w-4xl mx-auto px-4 pb-6"
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-yellow-500 text-[10px] font-black uppercase tracking-[0.35em] flex items-center gap-1.5">
          <Crown className="h-3 w-3" />
          Latest Founders
        </p>
        <Link
          to="/founders"
          data-testid="home-founders-see-all"
          className="text-yellow-400 hover:text-yellow-300 text-[10px] font-black uppercase tracking-[0.25em]"
        >
          See the wall →
        </Link>
      </div>

      <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {last3.map((f, i) => (
          <li key={`${f.outlaw_number || i}-${f.display_name}`}>
            {f.photo_url ? <MiniPortrait f={f} /> : <MiniPlate f={f} />}
          </li>
        ))}
      </ul>
    </section>
  );
};

/** Engraved gold nameplate for a Founder without a portrait. */
const MiniPlate = ({ f }) => (
  <div
    className="relative rounded-[3px] py-3 px-4"
    data-testid={`home-founder-plate-${f.outlaw_number || ''}`}
    style={{
      background: `
        repeating-linear-gradient(90deg, rgba(255,230,170,0.35) 0px, rgba(255,230,170,0.35) 1px, transparent 1px, transparent 3px),
        linear-gradient(180deg, #f4d67a 0%, #e0b148 30%, #b98726 55%, #d3a44a 78%, #f5dc95 100%)
      `,
      boxShadow: `
        0 2px 4px rgba(0,0,0,0.7),
        inset 0 0 0 1px rgba(180,120,40,0.6),
        inset 0 1px 1px rgba(255,240,190,0.8),
        inset 0 -2px 4px rgba(80,45,10,0.45)
      `,
    }}
  >
    {['top-1 left-1', 'top-1 right-1', 'bottom-1 left-1', 'bottom-1 right-1'].map((pos, i) => (
      <span
        key={i}
        aria-hidden
        className={`absolute ${pos} h-1.5 w-1.5 rounded-full`}
        style={{
          background: 'radial-gradient(circle at 35% 30%, #f3d078, #a67918 55%, #4d3308 100%)',
          boxShadow: '0 1px 1px rgba(0,0,0,0.7)',
        }}
      />
    ))}
    <span
      className="relative block text-center font-black leading-tight truncate"
      style={{
        fontFamily: 'Georgia, "Times New Roman", serif',
        color: '#1a0a04',
        fontSize: '14px',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        textShadow: '0 1px 0 rgba(255,235,180,0.55), 0 -1px 1px rgba(0,0,0,0.55)',
      }}
    >
      {f.display_name}
    </span>
    {f.month && (
      <span
        className="relative block text-center mt-0.5 italic"
        style={{
          fontFamily: 'Georgia, serif',
          color: '#4a2b12',
          fontSize: '9px',
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
          textShadow: '0 1px 0 rgba(255,235,180,0.4)',
        }}
      >
        Since {f.month}
      </span>
    )}
  </div>
);

/** Portrait-under-glass mini card for Founders with an uploaded photo. */
const MiniPortrait = ({ f }) => (
  <div
    className="relative rounded-[4px] p-[3px]"
    data-testid={`home-founder-portrait-${f.outlaw_number || ''}`}
    style={{
      background: 'linear-gradient(135deg, #f7dc8b 0%, #cd982e 32%, #8a6015 62%, #f5dc95 100%)',
      boxShadow: '0 3px 8px rgba(0,0,0,0.6), inset 0 1px 1px rgba(255,240,190,0.7)',
    }}
  >
    <div className="flex items-center gap-2">
      <div
        className="relative shrink-0 w-11 h-11 rounded-[2px] overflow-hidden"
        style={{ boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.9)' }}
      >
        <img src={f.photo_url} alt={f.display_name} className="w-full h-full object-cover" loading="lazy" />
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.22) 0%, transparent 55%)' }}
        />
      </div>
      <div className="min-w-0 flex-1 pr-2">
        <span
          className="block truncate font-black"
          style={{
            fontFamily: 'Georgia, serif',
            color: '#1a0a04',
            fontSize: '13px',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            textShadow: '0 1px 0 rgba(255,235,180,0.55)',
          }}
        >
          {f.display_name}
        </span>
        {f.month && (
          <span
            className="block italic"
            style={{
              fontFamily: 'Georgia, serif',
              color: '#4a2b12',
              fontSize: '9px',
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
            }}
          >
            Since {f.month}
          </span>
        )}
      </div>
    </div>
  </div>
);

export default HomeFoundersPreview;
